import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import type { FilterDefinition, FilterGroupDefinition, SurfaceSelection } from "../../types/filterDefinition";
import { ALL_FILTER_USAGES } from "../../types/filterDefinition";
import type { FiltersConfig } from "../../types/filtersConfig";
import { filtersConfigStatusMap } from "../../types/filtersConfig";
import type { OrderedSearchFilterInstance, SearchFilterInstance } from "../../types/searchFilterInstance";
import { normalizeCategories } from "../../utils/filterConfig";

import { isSingleFilterDefinition } from "./guards";
import { createPendingInstance } from "./pendingInstance";

/**
 * Either registration shape. The fold reads only the fields both declare — placement, gating, readiness and
 * `build` — so a group needs no special case here; `toInstances` already normalises its array return, and
 * `buildPending` is absent from a group by construction.
 */
type AnyFilterDefinition<TValues extends IPlainObject, TApiCategory extends string, TCtx> =
    | FilterDefinition<TValues, TApiCategory, TCtx, string, string>
    | FilterGroupDefinition<TValues, TApiCategory, TCtx, string, string>;

/**
 * The subset of a registry a surface actually shows, resolved purely by set containment:
 *  - the definition's category is in scope (or the surface scopes no categories),
 *  - the definition permits this surface's usage,
 *  - the surface grants every capability the definition requires.
 * Neither side names the other — a filter never names a page, a surface never names a key.
 */
export const selectDefinitions = <TValues extends IPlainObject, TApiCategory extends string, TCtx>(
    registry: readonly AnyFilterDefinition<TValues, TApiCategory, TCtx>[],
    selection: SurfaceSelection<string, string>,
): readonly AnyFilterDefinition<TValues, TApiCategory, TCtx>[] => {
    const { categories, grants } = selection;

    const isCategoryInScope = (definition: AnyFilterDefinition<TValues, TApiCategory, TCtx>): boolean =>
        !categories || normalizeCategories(definition.category).some((c) => categories.has(c));

    const permitsUsage = (definition: AnyFilterDefinition<TValues, TApiCategory, TCtx>): boolean =>
        (definition.usages ?? ALL_FILTER_USAGES).includes(selection.usage);

    const grantsRequirements = (definition: AnyFilterDefinition<TValues, TApiCategory, TCtx>): boolean =>
        (definition.requiresCapabilities ?? []).every((capability) => grants.has(capability));

    return registry.filter((d) => isCategoryInScope(d) && permitsUsage(d) && grantsRequirements(d));
};

type AssembleParams<TValues extends IPlainObject, TApiCategory extends string, TCtx, TDataSource extends string> = {
    readonly registry: readonly AnyFilterDefinition<TValues, TApiCategory, TCtx>[];
    readonly selection: SurfaceSelection<string, string>;
    readonly ctx: TCtx;
    /** Data sources whose fetch has resolved. A definition builds only once every `requires` entry is ready. */
    readonly ready: ReadonlySet<TDataSource>;
    /**
     * Data sources whose fetch is in flight; consulted only for `buildPending` opt-ins. A source absent
     * from this set degrades to the filter being absent (a disabled or errored fetch never strands a
     * loader) — the entity resolver owns wiring each source's in-flight signal.
     */
    readonly loading?: ReadonlySet<TDataSource>;
    /** Base `order` for a definition; a fanned-out group gets base + sub-index. */
    readonly orderOf: (definition: AnyFilterDefinition<TValues, TApiCategory, TCtx>) => number;
};

const toInstances = <T extends IPlainObject, A extends string>(
    built: SearchFilterInstance<T, A> | readonly SearchFilterInstance<T, A>[] | undefined,
): readonly SearchFilterInstance<T, A>[] => {
    if (built == null) {
        return [];
    }

    return Array.isArray(built) ? built : [built as SearchFilterInstance<T, A>];
};

/**
 * Folds a registry into a panel config. Three things fall out as emergent behaviour rather than special
 * cases: a filter that needs data it does not yet have simply does not appear (no hand-curated "initial"
 * branch; a `buildPending` opt-in shows an inert placeholder card instead while the source is in flight),
 * a single slow data source costs only its own filters (no whole-panel blanking), and status is a
 * function of what is *buildable* — `pending` until something can build, `ready` once every in-scope
 * definition can, `initial` in between. A `build()` that returns `undefined` is a permanent local veto
 * (e.g. no options), so it yields no instance yet never holds status back.
 *
 * The one deliberate exception: a definition that `seedsDefault(ctx)` on this surface and cannot build yet
 * holds the whole config at `pending` with an empty list. Consumers seed default values from the first
 * non-pending list, exactly once — a default-carrying filter arriving after that seed would never get its
 * default applied. A definition that seeds no default here does not hold: it self-gates like any other.
 */
export const assembleFiltersConfig = <
    TValues extends IPlainObject,
    TApiCategory extends string,
    TCtx,
    TDataSource extends string,
>({
    registry,
    selection,
    ctx,
    ready,
    loading,
    orderOf,
}: AssembleParams<TValues, TApiCategory, TCtx, TDataSource>): FiltersConfig<
    readonly OrderedSearchFilterInstance<TValues, TApiCategory>[]
> => {
    const selected = selectDefinitions(registry, selection);

    // An empty selection has nothing to wait for
    if (selected.length === 0) {
        return { filtersList: [], status: filtersConfigStatusMap.ready };
    }

    const isBuildable = (definition: AnyFilterDefinition<TValues, TApiCategory, TCtx>): boolean =>
        (definition.requires ?? []).every((source) => ready.has(source as TDataSource));

    if (selected.some((definition) => definition.seedsDefault?.(ctx) && !isBuildable(definition))) {
        return { filtersList: [], status: filtersConfigStatusMap.pending };
    }

    const buildable = selected.filter(isBuildable);

    // Stamped in place rather than spread into a copy: `build()` already returns a fresh instance, so copying
    // it only to add `order` is wasted, and it would defeat any future build cache — a reused instance wrapped
    // in a new object is a new identity to every memoized consumer downstream. Matches `withOrder`, which the
    // hand-written signals remainder already uses.
    const builtList = buildable.flatMap((definition) => {
        const base = orderOf(definition);

        return toInstances(definition.build(ctx)).map(
            (instance, subIndex): OrderedSearchFilterInstance<TValues, TApiCategory> =>
                Object.assign(instance, { order: base + subIndex }),
        );
    });

    const pendingList = selected.flatMap((definition): OrderedSearchFilterInstance<TValues, TApiCategory>[] => {
        if (isBuildable(definition) || !isSingleFilterDefinition(definition) || !definition.buildPending || !loading) {
            return [];
        }

        const missing = (definition.requires ?? []).filter((source) => !ready.has(source as TDataSource));

        if (!missing.every((source) => loading.has(source as TDataSource))) {
            return [];
        }

        const parts = definition.buildPending(ctx);

        return parts ? [{ ...createPendingInstance(definition, parts), order: orderOf(definition) }] : [];
    });

    const filtersList = [...builtList, ...pendingList];

    // Gated on buildability, not on `filtersList`: a veto contributes no instance, so testing the built
    // list would report `pending` forever once every buildable definition happens to veto.
    const status =
        buildable.length === 0
            ? filtersConfigStatusMap.pending
            : buildable.length === selected.length
              ? filtersConfigStatusMap.ready
              : filtersConfigStatusMap.initial;

    return { filtersList, status };
};
